import { startOfTomorrow } from 'date-fns'

import type { FbranchDiscountSlot } from '@/types/api'
import type { SlotInfo } from '@/types/product'
import { QueryStringKeys } from '@/constants/commons'

type UseProductTimeSlot = {
  slots: ReadonlyRef<FbranchDiscountSlot[]>
  maxSlotsCount: ReadonlyRef<number | undefined>
  isMoreShown: ReadonlyRef<boolean>
  isDateLabelShown: ReadonlyRef<boolean>
  link: ReadonlyRef<string | undefined>
}

export function useProductTimeSlot(opts: UseProductTimeSlot) {
  const { slots, maxSlotsCount, isMoreShown, link, isDateLabelShown } = opts

  const { t, te } = useI18n()

  const timeSlots = computed<SlotInfo[]>(() => {
    if (!slots.value || slots.value.length === 0) {
      return []
    }

    let foundDate = ''
    const todayDate = _formatDate(new Date())
    const tomorrowDate = _formatDate(startOfTomorrow())
    const otherDatePattern = isDateLabelShown.value ? getOtherDatePattern() : ''

    const formattedSlots = slots.value
      ?.map(slotInfo => {
        const slotDate = _formatDate(slotInfo.slot ?? '')

        if (foundDate !== slotDate) {
          foundDate = ''
        }

        const label = (() => {
          if (!isDateLabelShown.value) {
            return null
          }
          if (slotDate === todayDate && !foundDate) {
            return t('card.date.today')
          }
          if (slotDate === tomorrowDate && !foundDate) {
            return t('card.date.tomorrow')
          }
          if (slotDate !== todayDate && slotDate !== tomorrowDate && !foundDate) {
            return _formatDate(slotDate, otherDatePattern)
          }

          return null
        })()

        foundDate = slotDate

        return {
          ...slotInfo,
          type: 'time' as 'time' | 'more',
          label,
          ...(!!link.value && { link: `${link.value}?${QueryStringKeys.SLOT}=${slotInfo.slot}` }),
        }
      })
      .slice(0, maxSlotsCount.value)

    return isMoreShown.value
      ? formattedSlots.concat({ type: 'more' as 'time' | 'more', label: null, ...(!!link.value && { link: link.value }) })
      : formattedSlots
  })

  function _formatDate(rawDate: Date | string, pattern = 'yyyy/MM/dd') {
    return formatDate(rawDate, pattern)
  }

  function getOtherDatePattern(): string {
    return (te('card.date.other') && t('card.date.other')) || 'MMM.dd'
  }

  return { timeSlots }
}
